import { useTranslation } from "@outschool/localization";
import { onlineClassesPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { gql, useQueryWithPreviousData } from "@outschool/ui-apollo";
import React from "react";

import * as Env from "../../../../shared/Env";
import {
  CategoryPagesQuery,
  CategoryPagesQueryVariables
} from "../../../../shared/generated/graphql";
import { makeCardDefinition } from "../../../../shared/parentDashboard/cardTypes/Topic";
import { seededShuffle } from "../../../../shared/Random";
import { ParentDashboardListingsByActivityUids } from "../cardTypes/sharedQueries";
import * as Topic from "../cardTypes/Topic";
import TabContent from "./TabContent";

const RecentCategoryFragment = gql`
  fragment RecentCategoryFragment on CategoryPage {
    uid
    nameTitleCased
    nameSentenceCased
    slug
    title
    description
    searchFilters {
      adminTags
      age
      delivery
      dow
      enabledBooleanFilters
      endBy
      endByTime
      format
      fundingPrograms
      multiTermQuery
      order
      pricePerMeetingMax
      pricePerMeetingMin
      q
      startAfter
      startAfterTime
      theme
      time
      timeOfDay
      timeZone
    }
  }
`;

const RecentCategoryPagesQuery = gql`
  query CategoryPages($slugs: [String!]) {
    categoryPages(slugs: $slugs) {
      ...RecentCategoryFragment
    }
  }
  ${RecentCategoryFragment}
`;

export default function RecentTab({
  recentlyViewedCategories,
  recentlyViewedActivities,
  random
}: $TSFixMe) {
  const { t } = useTranslation(
    "client\\components\\parentDashboard\\tabs\\RecentTab"
  );

  // For classes, show classes you have recently viewed the class details page for. Show all classes viewed in the last 14 days, and if there are not at least 20 of those, then also start showing classes viewed more than 14 days ago until you get to at least 10 classes.
  const twoWeeksAgo = dayjs().subtract(2, "week");
  let recentlyViewedList = recentlyViewedActivities
    .filter((a: $TSFixMe) => dayjs(a.createdAt) > twoWeeksAgo)
    .slice(0, 30);
  if (recentlyViewedList.length < 10) {
    recentlyViewedList = recentlyViewedActivities.slice(0, 10);
  }

  const { loading, data } = useQueryWithPreviousData(
    ParentDashboardListingsByActivityUids,
    {
      skip: !!Env.FEATURE_FLAGS.DISABLE_OPTIONAL_QUERIES,
      variables: {
        activityUids: recentlyViewedList.map((a: $TSFixMe) => a.activityUid),
        skipRating: false,
        withRecentReviews: false
      },
      context: { doNotRetry: true }
    }
  );
  const listings = data?.listingsByActivityUids;

  // Include up to 5 carousels, each for a randomly chosen category that is in your top 10 most recently viewed categories.
  const tenCategories = recentlyViewedCategories.slice(0, 10);
  const fiveCategories = seededShuffle(random, tenCategories).slice(0, 5);
  const { data: categoriesData } = useQueryWithPreviousData<
    CategoryPagesQuery,
    CategoryPagesQueryVariables
  >(RecentCategoryPagesQuery, {
    variables: {
      slugs: fiveCategories.map(category => (category as any).categorySlug)
    }
  });
  const carousels = [];
  if (!categoriesData) {
    return null;
  }
  // @ts-ignore TS(2531): Object is possibly 'null'.
  for (let c of categoriesData.categoryPages) {
    delete c.__typename;
    // @ts-ignore TS(2531): Object is possibly 'null'.
    delete c.searchFilters.__typename;
    carousels.push(
      // @ts-ignore TS(2345): Argument of type 'Element' is not assignable to pa... Remove this comment to see the full error message
      <Topic.Component
        key={`carousel-category-${c.slug}`}
        /* eslint-disable-next-line i18next/no-literal-string */
        // @ts-ignore TS(2345): Argument of type '{ display: string; id: string; u... Remove this comment to see the full error message
        card={makeCardDefinition({
          ...c,
          display: "list",
          id: c.slug,
          url: onlineClassesPath(c.slug)
        })}
        trackingProperties={{}}
        loadAfterDelay={0}
        isCategory={true}
      />
    );
  }

  return (
    <TabContent
      title={t("Recently viewed classes and topics")}
      listings={listings}
      listingsLoading={loading}
      carousels={carousels}
    />
  );
}
